import { JSX, RefObject } from 'preact';
import { useState, useRef, useEffect, useCallback, useMemo } from 'preact/hooks';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { mergeRefs } from '../utils/PRIVATE_refUtils';
import { Layer } from '../UNSAFE_Layer';
import { Modal } from '../UNSAFE_Modal';
import { LogicalParentRefContext } from '../UNSAFE_Layer/LogicalParentRefContext';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import {
  getAutofocusFocusables,
  focusOn,
  focusWithin,
  getActiveElement,
  focusOnElementOrNearestAncestor
} from '../utils/PRIVATE_tabbableUtils';
import { normalizePosition } from '../utils/PRIVATE_floatingUtils';
import { RtlSide } from '../UNSAFE_Floating';
import { getClientHints } from '../utils/PRIVATE_clientHints';

// Hooks
import { useOutsideClick } from '../hooks/UNSAFE_useOutsideClick';
import { useAnimation } from '../hooks/UNSAFE_useAnimation';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useSwipe } from '../hooks/UNSAFE_useSwipe';

// Style imports
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { DrawerPopupVariantOptions } from './themes/DrawerPopupStyles.css';
import { colorInterpolations, ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { DrawerPopupRedwoodTheme } from './themes/redwood/DrawerPopupTheme';

// Types
type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;
type Placement = 'start' | 'end' | 'bottom';
type NormalizedPlacement = 'left' | 'right' | 'bottom';
type Status = 'unmounted' | 'initial' | 'opening' | 'closing';
type VisibilityStatus = 'hidden' | 'visible';
type DisplayModeHorizontal = 'overlay' | 'fullOverlay';
type OnCloseDetail = {
  reason: 'outsideClick' | 'escapeKey' | 'swipe';
};

type Props = IntrinsicProps &
  Pick<ColorProps, 'backgroundColor'> & {
    /**
     * Specifies whether the Drawer is open.
     */
    isOpen: boolean;
    /**
     * Specifies at what edge the Drawer opens.
     * Supported values are:
     * <p><code>start</code>, <code>end</code>, <code>bottom</code></p>
     * Default is <code>start</code>.
     */
    placement?: Placement;
    /**
     * Specifies modality of the Darwer.
     * Supported values are:
     * <p><code>modal</code>, <code>modeless</code></p>
     * Default is <code>modal</code>.
     */
    modality?: 'modal' | 'modeless';
    /**
     * Specifies callback triggered when a user tries to close a Drawer through UI interaction.
     * The parent should listen to this event and close the Drawer. If the parent fails to remove
     * the Popup, then no change will be done in the UI by the component.
     * Supported detail values are:
     * <p><code>outsideClick</code>, <code>escapeKey</code></p>, <code>swipe</code>
     */
    onClose?: (detail: OnCloseDetail) => void;
    /**
     * Specifies callback triggered after the animation ends.
     */
    onTransitionEnd?: (value: boolean) => void;
    /**
     * Optional ref for an element to focus on when component mounts.
     */
    autoFocusRef?: RefObject<{ focus: () => void }>;
    /**
     * Specifies guidance for screen readers.
     */
    'aria-label'?: string;
    /**
     * Specifies ID of an element used for guidance for screen readers.
     */
    'aria-labelledby'?: string;
    /**
     * Specifies ID of an element (or space separated IDs of multiple elements) that
     * describes the Drawer.
     */
    'aria-describedby'?: string;
  } & TestIdProps;

// Constants
const displayModefullWidthThreshold = 600;
const zero = '0px';
const duration = 250;

const getElementHeight = (element: HTMLDivElement): number => {
  return Math.round(element.offsetHeight);
};

const getElementWidth = (element: HTMLDivElement): number => {
  return Math.round(element.getBoundingClientRect().width);
};

/**
 * A drawer popup adds a single slide-in side content alongside some primary content to an application window.
 *
 * This drawer always overlays the page and can be placed at the 'start', 'end' or 'bottom' edge.
 */
export const DrawerPopup = forwardRef(
  (
    {
      children,
      isOpen = false,
      autoFocusRef,
      placement = 'start',
      modality = 'modal',
      onClose,
      onTransitionEnd,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      testId,
      ...props
    }: Props,
    ref: ForwardedRef<HTMLElement | null>
  ) => {
    // Initialize states
    const [status, _setStatus] = useState<Status>(isOpen ? 'initial' : 'unmounted');
    const _status = useRef<Status>(isOpen ? 'initial' : 'unmounted');
    const setStatus = (s: Status) => {
      _status.current = s;
      _setStatus(s);
    };
    const [visibility, setVisibility] = useState<VisibilityStatus>('hidden');
    const [isOutsideClickDisabled, setIsOutsideClickDisabled] = useState<boolean>(!isOpen);
    const [displayModeHorizontal, setDisplayModeHorizontal] =
      useState<DisplayModeHorizontal>('overlay');

    const prevStatusRef = useRef<Status>('unmounted');

    const rootRef = useRef<HTMLDivElement>(null);
    const launcherRef = useRef<HTMLElement | null>(null);

    const mergedRef = useMemo(() => mergeRefs(rootRef, ref), [rootRef, ref]);

    const isBottomPlacement = placement === 'bottom';
    const shouldReturnFocus = useRef<boolean>(false);

    // Animation config
    const animationStates = {
      opening: () => {
        if (isBottomPlacement) {
          return {
            from: {
              translateY: getTranslateY()
            },
            to: {
              translateY: zero
            },
            options: {
              duration: duration
            }
          };
        } else {
          return {
            from: {
              translateX: getTranslateX()
            },
            to: {
              translateX: zero
            },
            options: {
              duration: duration
            }
          };
        }
      },
      closing: () => {
        if (isBottomPlacement) {
          return {
            from: {
              translateY: zero
            },
            to: {
              translateY: getTranslateY()
            },
            options: {
              duration: duration
            }
          };
        } else {
          return {
            from: {
              translateX: zero
            },
            to: {
              translateX: getTranslateX()
            },
            options: {
              duration: duration
            }
          };
        }
      }
    };

    // testId support
    const testIdProps = useTestId(testId);

    // Normalizing position
    const { direction } = useUser();
    const getNormalizedPlacement = (): RtlSide => {
      return normalizePosition(placement, direction);
    };

    const getTranslateX = (): string => {
      return `${getNormalizedPlacement() === 'right' ? '' : '-'}${getElementWidth(
        rootRef.current as HTMLDivElement
      )}px`;
    };

    const getTranslateY = (): string => {
      return `${getElementHeight(rootRef.current as HTMLDivElement)}px`;
    };

    const setInitialFocus = () => {
      if (rootRef.current) {
        // Set focus to the first match:
        // 1. First element inside the drawer matching [autofocus]
        const autofocusItems: HTMLElement[] = getAutofocusFocusables(
          rootRef.current as HTMLElement
        );
        if (autofocusItems.length > 0) {
          focusOn(autofocusItems[0]);
          return;
        }
        // 2. Tabbable element inside the content element
        // 3. The drawer itself
        focusWithin(rootRef.current as HTMLElement);
      }
    };

    const returnFocusToLauncher = (): void => {
      if (shouldReturnFocus.current) {
        focusOnElementOrNearestAncestor(launcherRef.current as HTMLElement);
        shouldReturnFocus.current = false;
      }
    };

    // Remove style attribute left on the element by the Animation util
    const removeAnimationInlineStyles = useCallback(() => {
      rootRef.current?.style?.removeProperty('transform');
    }, []);

    // Animation
    const { nodeRef } = useAnimation<Status, HTMLDivElement>(status, {
      animationStates,
      isAnimatedOnMount: true,
      onAnimationEnd: () => {
        if (status === 'opening') {
          removeAnimationInlineStyles();
          setInitialFocus();
          onTransitionEnd?.(true);
        } else if (!isOpen) {
          const activeElement = getActiveElement() as HTMLElement;

          // We return focus to the launcher only if it is contained in drawer
          // Historically we were able to return focus to launcher also in 'content destroy cases'.
          // Case: if document.activeElement is removed from the DOM, browser moves focus to doucment body
          // That's why we are checking body in the condition below
          shouldReturnFocus.current =
            rootRef.current?.contains(activeElement) || activeElement === document.body || false;
          nodeRef(null);
          setVisibility('hidden');
          setStatus('unmounted');
          onTransitionEnd?.(false);
        }
      }
    });

    useEffect(() => {
      // 1. Ignore status update on initial render
      if (status === 'unmounted' && !isOpen && status !== prevStatusRef.current) {
        returnFocusToLauncher();
      }

      // 2. Mount visually hidden component
      // Component gets mounted, but will be visually hidden to avoid flickering
      // Flickering happens because it gets rendered before animation hook repositions
      // it in next render cycle
      else if (status === 'unmounted' && isOpen) {
        setStatus('initial');
      }

      // 3. Setup animation
      // Component has been mounted.
      // We will kick out animation as node is already present in DOM.
      // To avoid flickering, we still keep it visually hidden until the next render cycle.
      else if (status === 'initial' && isOpen) {
        nodeRef(rootRef.current);
        setIsOutsideClickDisabled(false);
        setStatus('opening');
      }

      // 3. Unhide component. It becomes visible.
      else if (status === 'opening' && isOpen) {
        setVisibility('visible');
      }

      // 4. Closing
      else if (!isOpen && status !== 'unmounted') {
        setIsOutsideClickDisabled(true);
        if (_status.current !== 'unmounted') {
          setStatus('closing');
        }
      }

      return () => {
        // remember the previous status
        prevStatusRef.current = status;
      };
    }, [isOpen, status, nodeRef]);

    // Destroy case handler
    useEffect(() => {
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (rootRef.current?.contains(getActiveElement() as HTMLElement) || false) {
          // In destroy case ModalManager is blocking returning of the focus.
          // We need to reschedule it to workaround that
          setTimeout(() => {
            focusOnElementOrNearestAncestor(launcherRef.current as HTMLElement);
          }, 0);
        }
      };
    }, []);

    // Launcher handler
    useEffect(() => {
      if (status === 'opening') {
        // Remember launcher
        launcherRef.current = getActiveElement() as HTMLElement;

        // Register F6 key handler. It is modeless only feature.
        if (modality === 'modeless') {
          launcherRef.current?.addEventListener('keydown', launcherKeyDownCallback);
        }
      }
      if (status === 'closing' && modality === 'modeless') {
        return () => {
          // Deregister F6 key handler
          launcherRef.current?.removeEventListener('keydown', launcherKeyDownCallback);
        };
      }
      return;
    }, [modality, status]);

    const launcherKeyDownCallback = (event: KeyboardEvent): void => {
      // Moves focus from launcher (must have focus) back to the Drawer
      if (launcherRef.current === getActiveElement() && event.code === 'F6') {
        // Prevent default F6 handlers.
        // F6 is a standard Chrome address bar shortcut on Windows.
        event.preventDefault();
        event.stopPropagation();

        // Focus on first tabbable in a container or container itself if there is none
        focusWithin(rootRef.current as HTMLElement);
      }
    };

    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        const launcherEl = launcherRef.current;
        switch (event.code) {
          case 'Escape': {
            onClose?.({ reason: 'escapeKey' });
            break;
          }
          case 'F6':
            if (modality === 'modeless') {
              // Prevent default F6 handlers.
              // F6 is a standard Chrome address bar shortcut on Windows.
              event.preventDefault();
              event.stopPropagation();
              // Focus launcher
              if (launcherEl) {
                focusOn(launcherEl);
              }
              break;
            }
        }
      },
      [onClose, modality]
    );

    // OutsideClick handler
    const handleOutsideClick = useCallback(() => {
      // Trigger onClose event with outside click detail
      onClose?.({ reason: 'outsideClick' });
    }, [onClose]);

    const insideRefs: RefObject<HTMLElement>[] = [rootRef];
    // launcher should be considered an "inside" element
    if (launcherRef.current && launcherRef.current !== document.body) {
      insideRefs.push(launcherRef);
    }

    useOutsideClick({
      isDisabled: isOutsideClickDisabled,
      ref: insideRefs,
      handler: handleOutsideClick
    });

    // Drawer placement
    const getDrawerPlacement = (): NormalizedPlacement => {
      if (isBottomPlacement) {
        return placement;
      } else {
        if (direction === 'rtl') {
          return placement === 'start' ? 'right' : 'left';
        } else {
          return placement === 'start' ? 'left' : 'right';
        }
      }
    };

    const clientHints = getClientHints();

    const getViewportWidth = useCallback((): number => {
      if (clientHints.platform === 'ios') {
        // On ios window.innerWidth is not recommended way of measuring the viewport
        return document.documentElement.clientWidth;
      }
      return window.innerWidth;
    }, [clientHints.platform]);

    // Window resize handler
    // Handles display mode: Overlay <> Full overlay (full width side drawers)
    const handleResize = useCallback(() => {
      const viewportWidth = getViewportWidth();
      if (displayModeHorizontal === 'overlay' && viewportWidth < displayModefullWidthThreshold) {
        setDisplayModeHorizontal('fullOverlay');
      }
      if (
        displayModeHorizontal === 'fullOverlay' &&
        viewportWidth > displayModefullWidthThreshold
      ) {
        setDisplayModeHorizontal('overlay');
      }
    }, [getViewportWidth, displayModeHorizontal]);

    useEffect(() => {
      const root = rootRef.current;
      if (root && status === 'opening') {
        const resizeObserver = new ResizeObserver(() => {
          handleResize();
        });
        resizeObserver.observe(document.body);
        return () => {
          resizeObserver.unobserve(document.body);
        };
      }
      return undefined;
    }, [status, handleResize]);

    const dispatchSwipeOnClose = useCallback(() => {
      onClose?.({ reason: 'swipe' });
    }, [onClose]);

    // OutsideClick handler
    const swipeHandler = useCallback(
      (event: any): void => {
        const swipeDir = event.direction;
        // LTR
        if (direction === 'ltr') {
          if (
            (placement === 'start' && swipeDir === 'left') ||
            (placement === 'end' && swipeDir === 'right')
          ) {
            dispatchSwipeOnClose();
          }
        }
        // RTL
        if (direction === 'rtl') {
          if (
            (placement === 'start' && swipeDir === 'right') ||
            (placement === 'end' && swipeDir === 'left')
          ) {
            dispatchSwipeOnClose();
          }
        }
        // Bottom
        if (placement === 'bottom' && swipeDir === 'down') {
          dispatchSwipeOnClose();
        }
      },
      [direction, placement, dispatchSwipeOnClose]
    );

    const swipeOptions = {
      threshold: 50,
      maximumTime: 750,
      tolerance: 40
    };
    const { swipeProps } = useSwipe(swipeHandler, swipeOptions);

    // Styles
    const { classes } = useComponentTheme<DrawerPopupVariantOptions>(DrawerPopupRedwoodTheme, {
      placement: getDrawerPlacement(),
      visibility,
      displayModeHorizontal:
        ['start', 'end'].indexOf(placement) > -1 ? displayModeHorizontal : undefined
    });

    const interpolations = [...Object.values(colorInterpolations)];
    const styleInterpolations = mergeInterpolations<ColorProps>(interpolations);
    const { ...styles } = styleInterpolations(props);

    const renderDrawer = () => {
      return (
        <FocusTrap autoFocusRef={autoFocusRef} restoreFocusRef={false}>
          <div
            ref={mergedRef}
            className={classes}
            style={styles}
            tabIndex={-1}
            role="dialog"
            onKeyDown={handleKeyDown}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            {...swipeProps}
            {...testIdProps}>
            {children}
          </div>
        </FocusTrap>
      );
    };

    const isMounted = status !== 'unmounted';
    if (isMounted) {
      // Modal vs. modelss upper wrapper
      if (modality === 'modal') {
        return (
          <LogicalParentRefContext.Provider value={launcherRef}>
            <Modal isOpen={isMounted}>{renderDrawer()}</Modal>
          </LogicalParentRefContext.Provider>
        );
      } else {
        return <Layer logicalParentRef={launcherRef}>{renderDrawer()}</Layer>;
      }
    }

    return null;
  }
);
